import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { api, Announcement } from '../api/client';
import { useStore } from '../stores/store';

const DAY_MS = 86400000;

// ¿Corresponde mostrar este aviso a este dispositivo, según su frecuencia y lo ya visto?
function isEligible(a: Announcement, seen: Record<string, number>): boolean {
  const last = seen[a.id];
  if (a.frequency === 'always') return true; // se ve en cada apertura
  if (a.frequency === 'everyNDays') {
    if (last == null) return true;
    return Date.now() - last >= Math.max(1, a.everyDays) * DAY_MS;
  }
  return last == null; // 'once': solo si nunca se vio
}

// Splash configurable (data/announcements.json). Evalúa UNA vez por carga: elige el primer
// aviso elegible y lo muestra como modal con cuenta regresiva antes de habilitar el cierre.
export function Announcements() {
  const seenWelcome = useStore((s) => s.seenWelcome);
  const markSeen = useStore((s) => s.markAnnouncementSeen);
  const [current, setCurrent] = useState<Announcement | null>(null);
  const [remaining, setRemaining] = useState(0);
  const evaluated = useRef(false);

  useEffect(() => {
    if (!seenWelcome || evaluated.current) return;
    evaluated.current = true;
    api
      .announcements()
      .then(({ announcements }) => {
        const seen = useStore.getState().announcementsSeen;
        const next = announcements.find((a) => a.type === 'modal' && isEligible(a, seen));
        if (next) setCurrent(next);
      })
      .catch(() => {
        /* sin avisos o sin red: no se muestra nada */
      });
  }, [seenWelcome]);

  // Cuenta regresiva (un solo intervalo por aviso). Con closeDelay=0 se puede cerrar ya.
  useEffect(() => {
    if (!current || current.closeDelay <= 0) return;
    setRemaining(current.closeDelay);
    const id = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          clearInterval(id);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [current]);

  if (!current) return null;
  const canClose = remaining <= 0;

  const close = () => {
    markSeen(current.id);
    setCurrent(null);
  };

  return (
    <Overlay onClick={canClose ? close : undefined}>
      <Card onClick={(e) => e.stopPropagation()}>
        {canClose && (
          <CloseX onClick={close} aria-label="Cerrar">
            ×
          </CloseX>
        )}
        {current.image && <img className="img" src={current.image} alt="" />}
        <h3>{current.title}</h3>
        <p>{current.body}</p>
        {current.link && (
          <a className="cta" href={current.link.url} target="_blank" rel="noopener noreferrer" onClick={close}>
            {current.link.label}
          </a>
        )}
        <button className="ok" disabled={!canClose} onClick={close}>
          {canClose ? 'Entendido' : `Podés cerrar en ${remaining}s`}
        </button>
      </Card>
    </Overlay>
  );
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.62);
  backdrop-filter: blur(2px);
`;

const Card = styled.div`
  position: relative;
  width: 100%;
  max-width: 360px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius};
  padding: 22px 20px 18px;
  text-align: center;
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.45);

  .img {
    width: 100%;
    border-radius: 10px;
    margin-bottom: 14px;
    display: block;
  }
  h3 {
    margin: 0 0 8px;
    font-size: 1.2rem;
    color: ${({ theme }) => theme.colors.accent};
  }
  p {
    margin: 0 0 16px;
    font-size: 0.95rem;
    line-height: 1.45;
    color: ${({ theme }) => theme.colors.text};
  }
  .cta {
    display: block;
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.accentText};
    font-weight: 700;
    text-decoration: none;
    border-radius: 999px;
    padding: 11px;
    margin-bottom: 10px;
  }
  .ok {
    width: 100%;
    background: transparent;
    border: 1px solid ${({ theme }) => theme.colors.borderStrong};
    color: ${({ theme }) => theme.colors.text};
    border-radius: 999px;
    padding: 10px;
    font-weight: 600;
    font-size: 0.9rem;
  }
  .ok:disabled {
    opacity: 0.55;
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const CloseX = styled.button`
  position: absolute;
  top: 8px;
  right: 10px;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 1.5rem;
  line-height: 1;
  padding: 4px 8px;
`;
